import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/brand/SeoHead";
import JsonLd from "@/components/brand/JsonLd";
import { COURSE_SCHEMA } from "@/lib/schema";
import GlowingGridBackground from "@/components/brand/GlowingGridBackground";
import GlassCard from "@/components/brand/GlassCard";
import GradientBorderCard from "@/components/brand/GradientBorderCard";
import FadeUp from "@/components/brand/FadeUp";
import { FloatingAccents } from "@/components/brand/AccentShapes";
import ProgramSnapshot from "@/components/sections/ProgramSnapshot";
import fblHero from "@/assets/brand/fbl_page_hero.png";

const LEARN_ITEMS = [
    { text: "Beginner AI prompting", color: "#EC4899" },
    { text: "Website planning", color: "#8B5CF6" },
    { text: "Personal portfolio structure", color: "#3B82F6" },
    { text: "Digital safety", color: "#14B8A6" },
    { text: "Light HTML/CSS concepts", color: "#FBBF24" },
    { text: "Design decision-making", color: "#EC4899" },
    { text: "Responsible use of AI-assisted tools", color: "#8B5CF6" },
    { text: "How to revise and improve a real project", color: "#3B82F6" },
];

const BUILD_BULLETS = [
    { text: "About section", color: "#EC4899" },
    { text: "Interests or creative focus", color: "#8B5CF6" },
    { text: "Project examples", color: "#3B82F6" },
    { text: "Skills they are learning", color: "#14B8A6" },
    { text: "Safe contact or parent-approved sharing option", color: "#FBBF24" },
    { text: "Privacy-aware public/private settings", color: "#EC4899" },
];

const WEEKS = [
    {
        n: 1,
        title: "Welcome, digital safety, portfolio planning",
        color: "#EC4899",
    },
    {
        n: 2,
        title: "Website structure, personal brand basics, beginner AI prompting",
        color: "#8B5CF6",
    },
    {
        n: 3,
        title: "Building the first version using Emergent",
        color: "#3B82F6",
    },
    {
        n: 4,
        title: "Light HTML/CSS edits, design choices, accessibility basics",
        color: "#14B8A6",
    },
    {
        n: 5,
        title: "Review, revise, privacy check, parent approval process",
        color: "#FBBF24",
    },
    {
        n: 6,
        title: "Final polish, student showcase option, confidence reflection",
        color: "#EC4899",
    },
];

export default function FutureBuildersLab() {
    return (
        <>
            <SeoHead
                title="Future Builders Lab | 6-Week Student Portfolio Website Program"
                description="Future Builders Lab is a 6-week pilot program where Lee County high school students build safe, parent-approved personal portfolio websites with AI-assisted tools."
                path="/future-builders-lab"
                image="/fbl_page_hero.png"
            />
            <JsonLd data={COURSE_SCHEMA} />

            {/* ===== Hero ===== */}
            <section
                className="vfs-section relative overflow-hidden"
                data-testid="fbl-hero"
                style={{ paddingTop: "clamp(2rem, 6vw, 4rem)" }}
            >
                <GlowingGridBackground accent="#3B82F6" opacity={0.2} />
                <div className="vfs-container relative">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <FadeUp>
                            <span className="vfs-eyebrow">
                                A Vibe for Success Program
                            </span>
                            <h1 className="vfs-h1 mt-5" data-testid="fbl-h1">
                                Future Builders Lab helps students{" "}
                                <span className="vfs-grad-text">
                                    build something real.
                                </span>
                            </h1>
                            <p
                                className="mt-6 text-lg leading-relaxed max-w-xl"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                A 6-week creative workforce-readiness pilot
                                where Lee County high school students build
                                safe, parent-approved personal portfolio
                                websites.
                            </p>
                            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                                <Link
                                    to="/student-interest"
                                    className="vfs-cta"
                                    data-testid="fbl-hero-interest"
                                >
                                    Submit Student Interest
                                </Link>
                                <Link
                                    to="/sponsor"
                                    className="vfs-cta-outline"
                                    data-testid="fbl-hero-sponsor"
                                >
                                    Sponsor a Student
                                </Link>
                            </div>
                        </FadeUp>
                        <FadeUp delay={0.1}>
                            <div className="relative">
                                <FloatingAccents />
                                <img
                                    src={fblHero}
                                    alt="Large browser window mockup with AI prompt panel illustrating a student portfolio being built."
                                    loading="eager"
                                    fetchPriority="high"
                                    width="900"
                                    height="700"
                                    className="relative w-full h-auto rounded-3xl"
                                    style={{
                                        boxShadow:
                                            "0 30px 80px rgba(59, 130, 246, 0.28)",
                                    }}
                                />
                            </div>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ===== Program Snapshot ===== */}
            <section className="vfs-section" data-testid="fbl-snapshot">
                <div className="vfs-container">
                    <div className="max-w-2xl mx-auto lg:mx-0">
                        <ProgramSnapshot />
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ===== Who It Is For ===== */}
            <section className="vfs-section" data-testid="fbl-who">
                <div className="vfs-container">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <h2 className="vfs-h2">Who it is for</h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Future Builders Lab is designed for high school
                                students who are curious, creative,
                                low-confidence, interested in digital tools, or
                                unsure where to start. Students do not need
                                coding experience.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ===== What Students Learn ===== */}
            <section
                className="vfs-section relative overflow-hidden"
                data-testid="fbl-learn"
            >
                <GlowingGridBackground accent="#8B5CF6" opacity={0.1} />
                <div className="vfs-container relative">
                    <FadeUp>
                        <h2 className="vfs-h2">What students learn</h2>
                    </FadeUp>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {LEARN_ITEMS.map((item, i) => (
                            <FadeUp key={item.text} delay={i * 0.04}>
                                <div
                                    className="vfs-glass p-5 h-full flex items-start gap-3"
                                    style={{ borderRadius: 16 }}
                                >
                                    <span
                                        className="mt-1.5 flex-shrink-0 inline-block w-2.5 h-2.5 rounded-full"
                                        style={{
                                            background: item.color,
                                            boxShadow: `0 0 10px ${item.color}90`,
                                        }}
                                        aria-hidden="true"
                                    />
                                    <span
                                        className="text-sm font-medium"
                                        style={{
                                            color: "var(--text-primary)",
                                        }}
                                    >
                                        {item.text}
                                    </span>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ===== What Students Build ===== */}
            <section className="vfs-section" data-testid="fbl-build">
                <div className="vfs-container">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <h2 className="vfs-h2">What students build</h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Each student builds a beginner-friendly
                                personal portfolio website that may include:
                            </p>
                        </FadeUp>
                        <FadeUp delay={0.08}>
                            <ul className="mt-7 space-y-3">
                                {BUILD_BULLETS.map((b) => (
                                    <li
                                        key={b.text}
                                        className="flex items-start gap-3"
                                    >
                                        <span
                                            className="mt-2 flex-shrink-0 inline-block w-2.5 h-2.5 rounded-full"
                                            style={{
                                                background: b.color,
                                                boxShadow: `0 0 10px ${b.color}80`,
                                            }}
                                            aria-hidden="true"
                                        />
                                        <span
                                            className="text-base sm:text-lg"
                                            style={{
                                                color: "var(--text-primary)",
                                            }}
                                        >
                                            {b.text}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ===== 6-Week Curriculum ===== */}
            <section
                className="vfs-section relative overflow-hidden"
                data-testid="fbl-curriculum"
            >
                <GlowingGridBackground accent="#EC4899" opacity={0.1} />
                <div className="vfs-container relative">
                    <FadeUp>
                        <h2 className="vfs-h2">6-week sample curriculum</h2>
                        <p
                            className="mt-3 text-sm italic"
                            style={{ color: "var(--text-secondary)" }}
                        >
                            This is a sample structure and may be adjusted for
                            the pilot.
                        </p>
                    </FadeUp>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {WEEKS.map((w, i) => (
                            <FadeUp key={w.n} delay={i * 0.06}>
                                <GlassCard
                                    accentColor={w.color}
                                    className="p-7 h-full"
                                >
                                    <span
                                        className="block leading-none mb-4 font-bold"
                                        style={{
                                            fontFamily:
                                                "'Space Grotesk', sans-serif",
                                            fontSize: "3.5rem",
                                            background: `linear-gradient(135deg, ${w.color} 0%, ${w.color}99 100%)`,
                                            WebkitBackgroundClip: "text",
                                            backgroundClip: "text",
                                            color: "transparent",
                                        }}
                                        aria-hidden="true"
                                    >
                                        {w.n.toString().padStart(2, "0")}
                                    </span>
                                    <p
                                        className="text-xs font-semibold uppercase tracking-wider mb-2"
                                        style={{
                                            color: "var(--text-secondary)",
                                        }}
                                    >
                                        Week {w.n}
                                    </p>
                                    <p
                                        className="text-base sm:text-lg font-medium leading-snug"
                                        style={{
                                            color: "var(--text-primary)",
                                        }}
                                    >
                                        {w.title}
                                    </p>
                                </GlassCard>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ===== Safety & Privacy ===== */}
            <section className="vfs-section" data-testid="fbl-safety">
                <div className="vfs-container">
                    <FadeUp>
                        <div
                            className="vfs-glass p-7 sm:p-10 max-w-3xl"
                            style={{
                                borderRadius: 20,
                                borderColor: "rgba(20, 184, 166, 0.35)",
                            }}
                        >
                            <h2 className="vfs-h2">Safety and privacy</h2>
                            <p
                                className="mt-5 text-base sm:text-lg leading-relaxed"
                                style={{ color: "var(--text-primary)" }}
                            >
                                Parent or guardian approval is required for
                                student participation. Student websites are
                                private or unlisted by default unless a parent
                                or guardian chooses otherwise. Students are
                                guided not to publish sensitive personal
                                information, private contact details, exact
                                addresses, school schedules, or other unsafe
                                identifying information.
                            </p>
                        </div>
                    </FadeUp>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ===== Program Outcome ===== */}
            <section className="vfs-section" data-testid="fbl-outcome">
                <div className="vfs-container">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <h2 className="vfs-h2">Program outcome</h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Every student who completes the pilot leaves
                                with a finished, parent-approved personal
                                portfolio website, beginner experience with
                                AI-assisted web tools, and the confidence that
                                comes from finishing a real project.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* ===== Bottom CTA ===== */}
            <section
                className="vfs-section"
                data-testid="fbl-bottom-cta"
                style={{ paddingTop: "0" }}
            >
                <div className="vfs-container">
                    <FadeUp>
                        <GradientBorderCard radius={24}>
                            <div className="p-8 sm:p-12 text-center">
                                <h2 className="vfs-h2 mb-7">
                                    Ready to take the next step?
                                </h2>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    <Link
                                        to="/student-interest"
                                        className="vfs-cta"
                                        data-testid="fbl-bottom-interest"
                                    >
                                        Submit Student Interest
                                    </Link>
                                    <Link
                                        to="/sponsor"
                                        className="vfs-cta-outline"
                                        data-testid="fbl-bottom-sponsor"
                                    >
                                        Sponsor a Student
                                    </Link>
                                </div>
                                <p
                                    className="mt-6 text-sm"
                                    style={{ color: "var(--text-secondary)" }}
                                >
                                    Pilot dates to be announced.
                                </p>
                            </div>
                        </GradientBorderCard>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
